import React from "react";
import { Input, NumberInput, SegmentedControl, Stack } from "@mantine/core";
import {
  DISCOUNT_TYPES,
  DiscountType,
  MAX_DISCOUNT_PERCENTAGE,
} from "../../types/discounts";

export interface DiscountValueFieldsProps {
  type: DiscountType;
  /** `number`, or "" while the input is empty (Mantine NumberInput shape). */
  amount: number | string;
  onTypeChange: (type: DiscountType) => void;
  onAmountChange: (amount: number | string) => void;
  /** Validation message rendered on the amount input. */
  amountError?: React.ReactNode;
  disabled?: boolean;
}

const TYPE_OPTIONS = [...DISCOUNT_TYPES];

/**
 * The type (Percentage / Fixed Amount) + amount pair shared by the discount
 * preset form (Settings) and the apply-discount modal (Transaction / Quick
 * Sale), so both edit a discount value the same way. Controlled; validation
 * lives with the caller (see `validateDiscountAmount`).
 */
export default function DiscountValueFields({
  type,
  amount,
  onTypeChange,
  onAmountChange,
  amountError,
  disabled = false,
}: DiscountValueFieldsProps) {
  const isPercentage = type === "Percentage";
  return (
    <Stack gap="md">
      <Input.Wrapper label="Type" required>
        <SegmentedControl
          fullWidth
          data={TYPE_OPTIONS}
          value={type}
          onChange={(value) => onTypeChange(value as DiscountType)}
          disabled={disabled}
          aria-label="Discount type"
        />
      </Input.Wrapper>
      <NumberInput
        label="Amount"
        required
        value={amount}
        onChange={onAmountChange}
        min={0}
        max={isPercentage ? MAX_DISCOUNT_PERCENTAGE : undefined}
        decimalScale={2}
        allowNegative={false}
        // Show the "cannot exceed 100%" validation message rather than
        // silently clamping the typed value on blur.
        clampBehavior="none"
        hideControls
        inputMode="decimal"
        leftSection={isPercentage ? undefined : "$"}
        rightSection={isPercentage ? "%" : undefined}
        error={amountError}
        disabled={disabled}
      />
    </Stack>
  );
}
